import { $ } from '../shared/index';
import { registerMarkdownEnhancer } from '../shared/app-hooks';
import type { BookEntry } from '../shared/types';

let booksCatalogPromise: Promise<BookEntry[]> | null = null;
let booksCatalogCache: BookEntry[] | null = null;

export function sanitizeBookPath(file: string) {
  return String(file || '').replace(/^\/+/, '').replace(/\.\.+/g, '').replace(/\\/g, '/');
}

export function normalizeAnchorText(value: string) {
  return String(value || '')
    .replace(/^#/, '')
    .replace(/\\/g, '')
    .replace(/[№.,:;!?()[\]{}'"«»“”]/g, '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function findBookAnchorTarget(reader: HTMLElement, rawAnchor: string, linkText: string) {
  const decodedAnchor = decodeURIComponent(String(rawAnchor || ''));
  const escapeSelector = window.CSS?.escape || ((value: string) => String(value).replace(/["\\#.;?+*~':!^$[\]()=>|/@]/g, '\\$&'));
  const direct = reader.querySelector(`#${escapeSelector(decodedAnchor)}`);
  if (direct) return direct;

  const normalizedAnchor = normalizeAnchorText(decodedAnchor);
  const normalizedLink = normalizeAnchorText(linkText);
  const headings = Array.from(reader.querySelectorAll('.book-reader-body h1, .book-reader-body h2, .book-reader-body h3, .book-reader-body h4'));

  const headingMatch = headings.find(heading => {
    const normalizedHeading = normalizeAnchorText(heading.textContent || '');
    return normalizedHeading === normalizedAnchor ||
      normalizedHeading === normalizedLink ||
      normalizedHeading.includes(normalizedLink) ||
      normalizedLink.includes(normalizedHeading);
  });
  if (headingMatch) return headingMatch;

  const strongs = Array.from(reader.querySelectorAll('.book-reader-body strong'));
  return strongs.find(strong => {
    const normalizedStrong = normalizeAnchorText(strong.textContent || '');
    return normalizedStrong === normalizedAnchor ||
      normalizedStrong === normalizedLink ||
      normalizedStrong.startsWith(normalizedLink) ||
      normalizedLink.startsWith(normalizedStrong);
  }) || null;
}

export function scrollBookToTarget(reader: HTMLElement, target: Element) {
  const readerRect = reader.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top = reader.scrollTop + targetRect.top - readerRect.top - 18;
  reader.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function bindBookLinks(reader: HTMLElement) {
  reader.querySelectorAll('.book-reader-body a').forEach(link => {
    const anchor = link as HTMLAnchorElement;
    const href = anchor.getAttribute('href') || '';

    if (href.startsWith('#')) {
      anchor.addEventListener('click', event => {
        event.preventDefault();
        const target = findBookAnchorTarget(reader, href.slice(1), anchor.textContent || '');
        if (target) scrollBookToTarget(reader, target);
      });
      return;
    }

    if (href.startsWith(`${DOC_ROUTE_PREFIX}/`) || href === DOC_ROUTE_PREFIX) {
      anchor.addEventListener('click', event => {
        event.preventDefault();
        void openDocRoute(href);
      });
      return;
    }

    if (/^https?:\/\//i.test(href) || href.startsWith('mailto:')) {
      anchor.target = '_blank';
      anchor.rel = 'noopener';
    }
  });
}

export async function ensureBooksCatalog(): Promise<BookEntry[]> {
  if (booksCatalogCache) return booksCatalogCache;
  if (!booksCatalogPromise) {
    booksCatalogPromise = fetch('/assets/books/books.json', { cache: 'no-cache' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const catalog = await response.json() as { books?: BookEntry[]; title?: string };
        const books = Array.isArray(catalog.books) ? catalog.books : [];
        books.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.title || '').localeCompare(String(b.title || '')));
        booksCatalogCache = books;
        return books;
      })
      .catch(() => {
        booksCatalogCache = [];
        return [];
      });
  }
  return booksCatalogPromise;
}

export function renderBooksList(books: BookEntry[]) {
  const list = $('books-list');
  if (!list) return;
  list.textContent = '';

  books.forEach((book, index) => {
    const btn = document.createElement('button');
    btn.className = 'book-list-item';
    btn.type = 'button';
    btn.dataset.bookIndex = String(index);
    btn.innerHTML = `<span class="book-list-title">${DOMPurify.sanitize(book.title || `Книга ${index + 1}`)}</span>
      ${book.subtitle ? `<span class="book-list-subtitle">${DOMPurify.sanitize(book.subtitle)}</span>` : ''}`;
    btn.addEventListener('click', () => {
      void loadBook(book, btn);
    });
    list.appendChild(btn);
  });
}

export async function loadBooksCatalog() {
  const list = $('books-list');
  const reader = $('books-reader');
  if (!list || !reader) return;
  if (list.dataset.loaded === 'true') return;

  list.innerHTML = '<div class="help-loading"><span class="help-loader"></span><span>Загрузка каталога...</span></div>';

  try {
    const books = await ensureBooksCatalog();
    renderBooksList(books);
    list.dataset.loaded = 'true';

    const response = await fetch('/assets/books/books.json', { cache: 'no-cache' });
    if (response.ok) {
      const catalog = await response.json() as { title?: string };
      if (catalog.title) {
        const titleEl = $('books-modal-title');
        if (titleEl) titleEl.textContent = catalog.title;
      }
    }

    if (books.length === 0) {
      reader.innerHTML = '<div class="books-empty-state"><span class="books-empty-icon" aria-hidden="true">◆</span><h3>Каталог пуст</h3><p>Добавьте записи в assets/books/books.json и положите Markdown-файлы книг в assets/books.</p></div>';
    }
  } catch {
    list.innerHTML = '<div class="help-error"><strong>Каталог недоступен.</strong><span>Проверьте файл assets/books/books.json.</span></div>';
  }
}

export async function loadBook(book: BookEntry, btn?: HTMLElement, anchor?: string) {
  const reader = $('books-reader');
  if (!reader) return;
  document.querySelectorAll('.book-list-item').forEach(item => item.classList.remove('active'));
  btn?.classList.add('active');

  reader.innerHTML = '<div class="help-loading"><span class="help-loader"></span><span>Загрузка книги...</span></div>';

  try {
    const file = sanitizeBookPath(book.file || '');
    if (!file) throw new Error('Missing file');
    const response = await fetch(`/assets/books/${file}`, { cache: 'no-cache' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const markdown = await response.text();
    const title = DOMPurify.sanitize(book.title || 'Книга');
    const subtitle = book.subtitle ? `<p>${DOMPurify.sanitize(book.subtitle)}</p>` : '';
    reader.innerHTML = `<header class="book-reader-header"><span>Текст автора</span><h3>${title}</h3>${subtitle}</header>
      <div class="book-reader-body">${DOMPurify.sanitize(marked.parse(markdown) as string)}</div>`;
    bindBookLinks(reader);

    if (anchor) {
      const target = findBookAnchorTarget(reader, anchor, '');
      if (target) {
        scrollBookToTarget(reader, target);
        return;
      }
    }

    reader.scrollTop = 0;
  } catch {
    reader.innerHTML = '<div class="help-error"><strong>Не удалось открыть книгу.</strong><span>Проверьте путь к файлу в books.json и наличие Markdown-файла в assets/books.</span></div>';
  }
}

async function findBookByFile(file: string): Promise<{ book: BookEntry; index: number } | null> {
  const books = await ensureBooksCatalog();
  const normalized = sanitizeBookPath(file).toLowerCase();
  const index = books.findIndex(book => sanitizeBookPath(book.file || '').toLowerCase() === normalized);
  if (index < 0) return null;
  return { book: books[index], index };
}

export const DOC_ROUTE_PREFIX = '/docs';

export function buildDocHref(file?: string, anchor?: string): string {
  if (!file) return DOC_ROUTE_PREFIX;
  const safeFile = sanitizeBookPath(file);
  const hash = anchor ? `#${encodeURIComponent(anchor)}` : '';
  return `${DOC_ROUTE_PREFIX}/${safeFile}${hash}`;
}

export function buildDocPublicUrl(file?: string, anchor?: string): string {
  return new URL(buildDocHref(file, anchor), window.location.origin).href;
}

export function parseDocLink(href: string): { file: string; anchor?: string } | null {
  const trimmed = String(href || '').trim();
  if (!trimmed.startsWith(`${DOC_ROUTE_PREFIX}/`)) return null;

  const rest = trimmed.slice(`${DOC_ROUTE_PREFIX}/`.length);
  const hashIdx = rest.indexOf('#');
  const file = sanitizeBookPath(hashIdx >= 0 ? rest.slice(0, hashIdx) : rest);
  if (!file) return null;

  const anchor = hashIdx >= 0 ? decodeURIComponent(rest.slice(hashIdx + 1)) : undefined;
  return { file, anchor };
}

export function getDocRouteFromLocation(loc: Pick<Location, 'pathname' | 'hash'> = window.location): string | null {
  const { pathname, hash } = loc;
  if (pathname === DOC_ROUTE_PREFIX || pathname === `${DOC_ROUTE_PREFIX}/`) {
    return DOC_ROUTE_PREFIX;
  }
  if (!pathname.startsWith(`${DOC_ROUTE_PREFIX}/`)) return null;

  const filePart = pathname.slice(`${DOC_ROUTE_PREFIX}/`.length).split('/')[0];
  if (!filePart) return DOC_ROUTE_PREFIX;

  const file = sanitizeBookPath(filePart);
  if (!file) return null;

  return `${DOC_ROUTE_PREFIX}/${file}${hash || ''}`;
}

export function syncDocRouteToLocation(href: string, replace = false) {
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method]({ docRoute: href }, document.title, href);
}

export function clearDocRouteFromLocation() {
  if (!window.location.pathname.startsWith(DOC_ROUTE_PREFIX)) return;
  window.history.replaceState({}, document.title, '/');
}

export async function openBooksModal() {
  $('books-modal')?.classList.remove('hidden');
  $<HTMLButtonElement>('books-modal-close')?.focus();
  await loadBooksCatalog();
}

export function closeBooksModal() {
  $('books-modal')?.classList.add('hidden');
  clearDocRouteFromLocation();
}

export async function openDocRoute(href: string): Promise<boolean> {
  const normalized = String(href || '').trim();
  if (normalized === DOC_ROUTE_PREFIX || normalized === `${DOC_ROUTE_PREFIX}/`) {
    await openBooksModal();
    syncDocRouteToLocation(DOC_ROUTE_PREFIX, true);
    return true;
  }

  const opened = await openDocDeepLink(normalized);
  if (opened) syncDocRouteToLocation(normalized, true);
  return opened;
}

export async function consumeDocRouteFromLocation(): Promise<boolean> {
  const href = getDocRouteFromLocation();
  if (!href) return false;
  return openDocRoute(href);
}

export function initDocRouteHandling() {
  window.addEventListener('popstate', () => {
    const href = getDocRouteFromLocation();
    if (!href) {
      $('books-modal')?.classList.add('hidden');
      return;
    }
    void openDocRoute(href);
  });
}

export async function openDocDeepLink(href: string): Promise<boolean> {
  const parsed = parseDocLink(href);
  if (!parsed) return false;

  await openBooksModal();

  const match = await findBookByFile(parsed.file);
  if (!match) return false;

  const list = $('books-list');
  const btn = list?.querySelector(`[data-book-index="${match.index}"]`) as HTMLElement | null;
  await loadBook(match.book, btn || undefined, parsed.anchor);
  return true;
}

export function bindConversationDocLinks(container: HTMLElement) {
  container.querySelectorAll(`a[href^="${DOC_ROUTE_PREFIX}"]`).forEach(link => {
    const anchor = link as HTMLAnchorElement;
    if (anchor.dataset.docLinkBound === '1') return;
    anchor.dataset.docLinkBound = '1';
    anchor.classList.add('conversation-doc-link');
    anchor.addEventListener('click', event => {
      event.preventDefault();
      void openDocRoute(anchor.getAttribute('href') || '');
    });
  });
}

// shared/ui runs this after each markdown render instead of importing kb/*
// (ADR-002 phase C inversion).
registerMarkdownEnhancer(bindConversationDocLinks);
